import { Component, createRef } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  DIVIDER_GRADIENT, FourCorners, INPUT_STYLE, CONTENT_BG_DEFAULT,
} from '../../lib/medievalTheme';
import { playUI, UI } from '../../utils/arena/uiSounds';
import {
  CODEX_FILTERS,
} from '../../utils/game/codexEntries';
import { rulesCatalog, rulesLabel } from '../../localization/rules';
import { t } from '../../localization/t';
import { getRulesLanguage, subscribeRulesLanguage } from '../../localization/preferences';
import { buildShortcutSections } from '../../utils/game/shortcutSections';
import { renderTextWithTooltips } from '../CardInspector';
import { IconTome } from '../ui/icons';
import { pushDismiss, removeDismiss } from '../../utils/escStack';

const KBD_STYLE = {
  display: 'inline-block',
  padding: '0.0625cqw 0.375cqw',
  background: 'linear-gradient(180deg, rgba(40,34,24,0.9) 0%, rgba(20,17,12,0.95) 100%)',
  border: '1px solid rgba(180,140,60,0.35)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.5625cqw',
  fontFamily: 'ui-monospace, "SF Mono", "Cascadia Mono", monospace',
  fontWeight: 700,
  color: '#d4a843',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)',
  minWidth: '1.25cqw',
  textAlign: 'center',
  lineHeight: 1.4,
  letterSpacing: '0.03em',
};

// Solid base layer behind the texture wash. Matches the deck notes
// panel so the codex reads as the same physical "tome" surface.
const CODEX_PANEL_BASE = '#0c0a08';

// Strip the codex's internal `((Reference))` and `))emphasis((` markup so
// the inner text reads naturally. Bare-word keyword matching inside
// renderTextWithTooltips then picks up the references and attaches the
// same hover-popover / read-more modal stack used in CardInspector.
function stripCodexMarkup(text) {
  return text
    .replace(/\(\(([^()\n]+?)\)\)/g, '$1')
    .replace(/\)\)([^()\n]+?)\(\(/g, '$1');
}

function ChevronIcon({ open }) {
  return (
    <svg style={{ width: '0.75cqw', height: '0.75cqw', transition: 'transform 0.2s ease',
      transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}
      viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 2.5 8 6l-4 3.5" />
    </svg>
  );
}

function CodexBody({ description, language = 'en' }) {
  return (
    <div lang={language} className="text-[0.6875cqw] leading-[1.55]" style={{ color: TEXT_BODY }}>
      {renderTextWithTooltips(stripCodexMarkup(description), 0, language)}
    </div>
  );
}

function ShortcutsContent({ query }) {
  const sections = buildShortcutSections();
  const q = query.trim().toLowerCase();
  const filtered = q
    ? sections
        .map((s) => ({ ...s, items: s.items.filter((it) => it.desc.toLowerCase().includes(q)) }))
        .filter((s) => s.items.length > 0)
    : sections;

  if (filtered.length === 0) {
    return (
      <div className="text-center text-[0.625cqw] py-6"
        style={{ color: TEXT_MUTED, fontStyle: 'italic' }}>
        No shortcuts match that search.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-1 py-1.5">
      <div className="text-[0.625cqw] leading-snug" style={{ color: TEXT_MUTED }}>
        Card shortcuts act on the hovered card or marquee selection.
      </div>
      {filtered.map((section) => (
        <div key={section.label}>
          <div className="text-[0.5625cqw] font-semibold mb-1.5"
            style={{ color: `${GOLD} 0.55)`, textShadow: `0 0 10px ${GOLD} 0.15)` }}>
            {t(section.label)}
          </div>
          <div className="flex flex-col gap-1">
            {section.items.map((item) => (
              <div key={item.desc} className="flex items-start gap-2 px-1.5 py-1 rounded-md"
                style={{ background: 'rgba(0,0,0,0.18)', border: `1px solid ${GOLD} 0.08)` }}>
                <span className="shrink-0 flex items-center gap-0.5" style={{ minWidth: '4.5cqw' }}>
                  {item.keys.map((k, i) => (
                    <span key={i}>
                      {i > 0 ? (
                        <span className="text-[0.5625cqw] mx-0.5" style={{ color: 'rgba(166,160,155,0.3)' }}>+</span>
                      ) : null}
                      <span style={KBD_STYLE}>{k}</span>
                    </span>
                  ))}
                </span>
                <span className="text-[0.6875cqw] leading-snug pt-0.5" style={{ color: TEXT_BODY }}>
                  {item.desc}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

class CodexEntryRow extends Component {
  constructor(props) {
    super(props);
    this.ref = createRef();
  }
  componentDidUpdate(prev) {
    const becameSelected = this.props.selected && !prev.selected;
    const becameFocused = this.props.focused && !prev.focused;
    if ((becameSelected || becameFocused) && this.ref.current) {
      this.ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
  render() {
    const { entry, selected, focused, onSelect, onFocus } = this.props;
    const highlighted = selected || focused;
    return (
      <div ref={this.ref} className="relative" data-codex-key={entry.keyword}>
        <button type="button" onClick={onSelect} onMouseEnter={onFocus}
          className="w-full flex items-center gap-2 px-2 py-1.5 cursor-pointer text-left transition-colors"
          style={{
            background: selected ? `${GOLD} 0.14)` : focused ? `${GOLD} 0.07)` : 'transparent',
            color: highlighted ? TEXT_PRIMARY : TEXT_BODY,
            borderRadius: 'var(--radius-md)',
            outline: focused && !selected ? `1px solid ${GOLD} 0.3)` : 'none',
            outlineOffset: '-1px',
          }}>
          <span style={{ color: selected ? ACCENT_GOLD : focused ? `${GOLD} 0.7)` : `${GOLD} 0.45)`, display: 'inline-flex' }}>
            <ChevronIcon open={selected} />
          </span>
          <span className="text-[0.75cqw] font-semibold tracking-wide truncate"
            style={{ textShadow: highlighted ? `0 0 10px ${GOLD} 0.18)` : 'none' }}>
            {t(entry.label)}
          </span>
          {entry.isKeywordAbility ? (
            <span className="ml-auto text-[0.5cqw] shrink-0"
              style={{
                color: `${GOLD} 0.65)`, padding: '0.0625cqw 0.375cqw',
                border: `1px solid ${GOLD} 0.25)`, borderRadius: 'var(--radius-sm)',
                background: 'rgba(0,0,0,0.25)',
              }}>
              {rulesLabel('Keyword', entry.language)}
            </span>
          ) : null}
        </button>
        <AnimatePresence initial={false}>
          {selected ? (
            <motion.div key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.2, 0.7, 0.3, 1] }}
              style={{ overflow: 'hidden' }}>
              <div className="px-2 pt-1 pb-3"
                style={{ borderLeft: `1px solid ${GOLD} 0.18)`, marginLeft: '0.625cqw' }}>
                <div className="pl-3">
                  <CodexBody description={entry.description} language={entry.language} />
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }
}

export default class GameCodexPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      query: '',
      filter: 'all',
      selectedKey: null,
      focusedKey: null,
      language: getRulesLanguage(),
    };
    this.listRef = createRef();
    this.inputRef = createRef();
    this.panelRef = createRef();
    // Keyboard-vs-mouse mode tracking. Scrolling the list under a
    // stationary cursor fires phantom mouseenter events on whichever
    // row passes beneath; without this gate, those events would yank
    // the keyboard cursor back to wherever the mouse happens to be.
    this._lastKeyboardNav = 0;
    this._lastMouseMove = 0;
  }

  componentDidMount() {
    this.unsubscribeLanguage = subscribeRulesLanguage(language => this.setState({ language }));
    window.addEventListener('keydown', this.handleGlobalKey, true);
    window.addEventListener('mousemove', this.markMouseMove, true);
    window.addEventListener('mousedown', this.handleOutsideMouseDown, true);
    if (this.props.open) this._escDismissId = pushDismiss(this._dismissEscape);
  }

  componentWillUnmount() {
    this.unsubscribeLanguage?.();
    window.removeEventListener('keydown', this.handleGlobalKey, true);
    window.removeEventListener('mousemove', this.markMouseMove, true);
    window.removeEventListener('mousedown', this.handleOutsideMouseDown, true);
    removeDismiss(this._escDismissId);
  }

  _dismissEscape = () => {
    if (this.state.selectedKey) {
      playUI(UI.CLICK_SOFT, { volume: 0.5 });
      this.setState({ selectedKey: null });
    } else {
      playUI(UI.CLOSE);
      this.props.onToggle();
    }
  };

  // Close on any mousedown outside the codex panel. Keyword hover
  // popovers and the keyword "read more" modal are portaled out of
  // panelRef into .app-canvas, so we whitelist [data-tooltip-popover]
  // and [role="dialog"] to avoid closing the codex when a user clicks
  // inside its own tooltip subtree.
  handleOutsideMouseDown = (e) => {
    if (!this.props.open) return;
    const panel = this.panelRef.current;
    if (!panel) return;
    const t = e.target;
    if (panel.contains(t)) return;
    if (t.closest && (t.closest('[data-tooltip-popover]') || t.closest('[role="dialog"]'))) return;
    playUI(UI.CLOSE);
    this.props.onToggle();
  };

  markMouseMove = () => {
    this._lastMouseMove = performance.now();
  };

  acceptHoverFocus = () => this._lastMouseMove >= this._lastKeyboardNav;

  markKeyboardNav = () => {
    this._lastKeyboardNav = performance.now();
  };

  componentDidUpdate(prev) {
    if (this.props.open && !prev.open) {
      requestAnimationFrame(() => { this.inputRef.current?.focus(); });
      this._escDismissId = pushDismiss(this._dismissEscape);
    }
    if (!this.props.open && prev.open) {
      this.setState({ query: '', selectedKey: null, focusedKey: null });
      removeDismiss(this._escDismissId);
    }
  }

  getVisibleEntries() {
    const { query, filter, language } = this.state;
    if (filter === 'shortcuts') return [];
    return rulesCatalog.entries(query, filter, language);
  }

  shiftFilter = (dir) => {
    const idx = CODEX_FILTERS.findIndex((f) => f.id === this.state.filter);
    const nextIdx = (idx + dir + CODEX_FILTERS.length) % CODEX_FILTERS.length;
    const nextId = CODEX_FILTERS[nextIdx].id;
    if (nextId === this.state.filter) return;
    playUI(UI.CLICK_SOFT, { volume: 0.5 });
    this.setState({ filter: nextId, focusedKey: null, selectedKey: null });
  };

  handleGlobalKey = (e) => {
    const isFind = (e.key === 'f' || e.key === 'F') && (e.metaKey || e.ctrlKey) && !e.altKey;
    if (isFind) {
      e.preventDefault();
      e.stopPropagation();
      if (!this.props.open) {
        playUI(UI.OPEN);
        this.props.onToggle();
      } else {
        this.inputRef.current?.focus();
        this.inputRef.current?.select();
      }
      return;
    }

    if (!this.props.open) return;
    const { selectedKey, focusedKey, query, filter } = this.state;
    const entries = this.getVisibleEntries();
    const inInput = e.target === this.inputRef.current;

    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      e.stopPropagation();
      this.markKeyboardNav();
      this.shiftFilter(e.key === 'ArrowRight' ? 1 : -1);
      return;
    }

    // On the Shortcuts tab there's no list cursor. Arrows scroll the
    // panel body so the user can browse a long shortcut list with the
    // keyboard.
    if (filter === 'shortcuts') {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        this.scrollBody(e.key === 'ArrowDown' ? 1 : -1);
      }
      return;
    }

    if (e.key === 'Backspace' && selectedKey && (!inInput || query.length === 0)) {
      e.preventDefault();
      e.stopPropagation();
      playUI(UI.CLICK_SOFT, { volume: 0.5 });
      this.setState({ selectedKey: null });
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (entries.length === 0) return;
      e.preventDefault();
      e.stopPropagation();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      // If the currently-selected entry is open and its body extends
      // past the visible viewport, scroll the body instead of jumping
      // to the next entry. Lets the user read long descriptions with
      // the keyboard.
      if (selectedKey && this.shouldScrollOpenBody(dir)) {
        this.scrollBody(dir);
        return;
      }
      const currentIdx = entries.findIndex((x) => x.keyword === (focusedKey || selectedKey));
      const nextIdx = currentIdx === -1
        ? (dir === 1 ? 0 : entries.length - 1)
        : (currentIdx + dir + entries.length) % entries.length;
      this.markKeyboardNav();
      this.setState({ focusedKey: entries[nextIdx].keyword });
      return;
    }

    if (e.key === 'Enter') {
      const key = focusedKey || (entries[0] && entries[0].keyword);
      if (!key) return;
      e.preventDefault();
      e.stopPropagation();
      playUI(UI.CLICK_SOFT, { volume: 0.6 });
      this.setState((s) => ({
        selectedKey: s.selectedKey === key ? null : key,
        focusedKey: key,
      }));
    }
  };

  scrollBody = (dir) => {
    const list = this.listRef.current;
    if (!list) return;
    this.markKeyboardNav();
    list.scrollBy({ top: dir * 60, behavior: 'smooth' });
  };

  // Prefer scrolling over jumping focus only when the user is sitting on
  // the currently-open row AND that row's body extends past the visible
  // viewport in the arrow direction. Once the body is fully read in that
  // direction, the next press advances focus normally.
  shouldScrollOpenBody = (dir) => {
    const { selectedKey, focusedKey } = this.state;
    if (!selectedKey || focusedKey !== selectedKey) return false;
    const list = this.listRef.current;
    if (!list) return false;
    const row = list.querySelector(`[data-codex-key="${CSS.escape(selectedKey)}"]`);
    if (!row) return false;
    const listRect = list.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    if (dir === 1) return rowRect.bottom > listRect.bottom + 4;
    return rowRect.top < listRect.top - 4;
  };

  selectEntry = (keyword) => {
    playUI(UI.CLICK_SOFT, { volume: 0.6 });
    this.markKeyboardNav();
    this.setState((s) => ({
      selectedKey: s.selectedKey === keyword ? null : keyword,
      focusedKey: keyword,
    }));
  };

  setFilter = (id) => {
    if (id === this.state.filter) return;
    playUI(UI.CLICK_SOFT, { volume: 0.5 });
    this.setState({ filter: id, focusedKey: null });
  };

  handleQueryInput = (e) => {
    this.setState({ query: e.currentTarget.value, focusedKey: null });
  };

  handleToggle = () => {
    playUI(this.props.open ? UI.CLOSE : UI.OPEN);
    this.props.onToggle();
  };

  render() {
    const { open } = this.props;
    const { query, filter, selectedKey, focusedKey, language } = this.state;
    const entries = this.getVisibleEntries();
    const t = value => rulesLabel(value, language);

    return (
      <div ref={this.panelRef}
        data-tutorial="board-compendium"
        className="fixed left-3 bottom-3 z-[1001]"
        style={{
          width: open ? '28cqw' : '2.5cqw',
          height: open ? '36cqw' : '2.5cqw',
          transition: 'width 0.32s cubic-bezier(0.2,0.7,0.3,1), height 0.32s cubic-bezier(0.2,0.7,0.3,1)',
        }}>
        <div className="relative w-full h-full"
          style={{
            background: open
              ? `${CONTENT_BG_DEFAULT}, ${CODEX_PANEL_BASE}`
              : CODEX_PANEL_BASE,
            backgroundBlendMode: open ? 'overlay, normal' : 'normal',
            border: `1px solid ${GOLD} ${open ? '0.6' : '0.35'})`,
            borderRadius: 'var(--radius-xl)',
            boxShadow: open
              ? [
                  '0 32px 80px rgba(0,0,0,0.7)',
                  `0 0 60px ${GOLD} 0.18)`,
                  `inset 0 0 0 1px ${GOLD} 0.22)`,
                  'inset 0 0 50px rgba(0,0,0,0.55)',
                ].join(', ')
              : '0 12px 28px rgba(0,0,0,0.45), 0 0 16px rgba(180,140,60,0.04)',
            isolation: 'isolate',
          }}>
          <FourCorners color={`${GOLD} 0.65)`} />

          <AnimatePresence initial={false}>
            {!open ? (
              <motion.button key="btn" type="button" onClick={this.handleToggle}
                className="absolute inset-0 flex items-center justify-center cursor-pointer transition-colors"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.12, duration: 0.16 } }}
                exit={{ opacity: 0, transition: { duration: 0.08 } }}
                style={{ color: `${GOLD} 1)`, background: 'transparent', border: 'none' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = ACCENT_GOLD; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = `${GOLD} 1)`; }}
                aria-label={t('Compendium')}>
                <IconTome size="1.375cqw" />
              </motion.button>
            ) : (
              <motion.div key="panel" className="absolute inset-0 flex flex-col"
                style={{ overflow: 'hidden', borderRadius: 'var(--radius-xl)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.18, duration: 0.18 } }}
                exit={{ opacity: 0, transition: { duration: 0.08 } }}>
                {/* Header */}
                <div className="flex items-center px-3 shrink-0"
                  style={{ height: '2.25cqw', borderBottom: `1px solid ${GOLD} 0.12)` }}>
                  <span style={{ color: ACCENT_GOLD, display: 'inline-flex', marginRight: '0.5cqw' }}>
                    <IconTome size="1.125cqw" />
                  </span>
                  <span className="arena-heading text-[1cqw] tracking-wide"
                    style={{ color: `${GOLD} 0.85)`, textShadow: `0 0 12px ${GOLD} 0.18)` }}>
                    {t('Compendium')}
                  </span>
                  <span className="ml-2 text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
                    {filter === 'shortcuts' ? t('Keyboard Shortcuts') : t('Rules & Keywords')}
                  </span>
                  <button type="button" onClick={this.handleToggle}
                    className="ml-auto cursor-pointer flex items-center justify-center transition-colors"
                    style={{
                      width: '1.5cqw', height: '1.5cqw', color: `${GOLD} 0.5)`,
                      background: 'transparent', border: 'none', borderRadius: 'var(--radius-sm)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; e.currentTarget.style.color = `${GOLD} 0.95)`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = `${GOLD} 0.5)`; }}
                    aria-label={t('Close')}>
                    <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 14 14" fill="none"
                      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                      <path d="M3 3 11 11M11 3 3 11" />
                    </svg>
                  </button>
                </div>

                {/* Search + filter */}
                <div className="px-3 pt-2.5 pb-2 shrink-0" style={{ borderBottom: `1px solid ${GOLD} 0.08)` }}>
                  <div className="relative">
                    <svg style={{
                      position: 'absolute', left: '0.5cqw', top: '50%', transform: 'translateY(-50%)',
                      width: '0.8125cqw', height: '0.8125cqw', color: `${GOLD} 0.45)`, pointerEvents: 'none',
                    }} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"
                      strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="6" cy="6" r="4" />
                      <path d="m9 9 3 3" />
                    </svg>
                    <input ref={this.inputRef} type="text" value={query}
                      onInput={this.handleQueryInput}
                      aria-label={filter === 'shortcuts' ? t('Search shortcuts...') : t('Search the compendium...')}
                      placeholder={filter === 'shortcuts' ? t('Search shortcuts...') : t('Search the compendium...')}
                      className="w-full text-[0.6875cqw] outline-none"
                      style={{
                        ...INPUT_STYLE,
                        height: '1.625cqw',
                        paddingLeft: '1.75cqw',
                        paddingRight: '0.625cqw',
                        color: TEXT_PRIMARY,
                      }} />
                  </div>
                  <div className="flex gap-1 mt-2">
                    {CODEX_FILTERS.map((f) => {
                      const active = f.id === filter;
                      return (
                        <button key={f.id} type="button" onClick={() => this.setFilter(f.id)}
                          className="text-[0.5625cqw] cursor-pointer transition-colors"
                          style={{
                            padding: '0.1875cqw 0.625cqw', borderRadius: 'var(--radius-sm)',
                            background: active ? `${GOLD} 0.18)` : 'transparent',
                            border: `1px solid ${active ? `${GOLD} 0.35)` : `${GOLD} 0.12)`}`,
                            color: active ? TEXT_PRIMARY : TEXT_MUTED,
                            textShadow: active ? `0 0 8px ${GOLD} 0.2)` : 'none',
                          }}>
                          {t(f.label)}
                        </button>
                      );
                    })}
                    {filter !== 'shortcuts' ? (
                      <span className="ml-auto text-[0.5cqw] self-center"
                        style={{ color: TEXT_MUTED }}>
                        {entries.length} {entries.length === 1 ? t('entry') : t('entries')}
                      </span>
                    ) : null}
                  </div>
                </div>

                {/* Body */}
                <div ref={this.listRef} className="flex-1 overflow-y-auto px-2 py-1.5">
                  {filter === 'shortcuts' ? (
                    <ShortcutsContent query={query} />
                  ) : entries.length === 0 ? (
                    <div className="text-center text-[0.625cqw] py-6"
                      style={{ color: TEXT_MUTED, fontStyle: 'italic' }}>
                      {t('No entries found.')}
                    </div>
                  ) : entries.map((entry) => (
                    <CodexEntryRow key={entry.keyword} entry={entry}
                      selected={entry.keyword === selectedKey}
                      focused={entry.keyword === focusedKey}
                      onSelect={() => this.selectEntry(entry.keyword)}
                      onFocus={() => {
                        if (!this.acceptHoverFocus()) return;
                        this.setState({ focusedKey: entry.keyword });
                      }} />
                  ))}
                </div>

                {/* Footer ornament */}
                <div className="shrink-0 h-px mx-3 mb-2" style={{ background: DIVIDER_GRADIENT, opacity: 0.6 }} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }
}
